/**
 * History page showing user's session history
 */
import { Link } from 'react-router-dom';
import { History, Clock, CheckCircle, Loader2 } from 'lucide-react';
import { Alert, AlertDescription, Button, Card, CardContent } from '@/components/ui';
import { useLanguage, useIsAuthenticated } from '@/store';
import { useSessionHistory, useSessionStatistics } from '@/hooks';
import { getCategoryLabel } from '@/utils/categories';
import { getHistoryTranslation } from '@/utils/translations';
import { formatDate } from '@/utils/locale';
import type { Language, SessionHistoryLocalizedItem } from '@/types';
import { ROUTES } from '@/config/routes';

function formatDuration(seconds: number): string {
  const hours = Math.floor(seconds / 3600);
  const minutes = Math.floor((seconds % 3600) / 60);
  if (hours > 0) {
    return `${hours}h ${minutes}m`;
  }
  return `${minutes} min`;
}

function formatPlayedDate(dateString: string, language: Language): string {
  return formatDate(dateString, language, { month: 'short', day: 'numeric', year: 'numeric' });
}

export function HistoryPage() {
  const language = useLanguage();
  const isAuthenticated = useIsAuthenticated();
  const { data: history, isLoading: historyLoading, error: historyError } = useSessionHistory();
  const { data: stats, isLoading: statsLoading } = useSessionStatistics();

  // Redirect message for unauthenticated users
  if (!isAuthenticated) {
    return (
      <div className="container py-16 text-center">
        <History className="h-16 w-16 mx-auto text-muted-foreground mb-4" />
        <h1 className="text-2xl font-bold mb-4">
          {getHistoryTranslation('signInToView', language)}
        </h1>
        <p className="text-muted-foreground mb-6">
          {getHistoryTranslation('signInDesc', language)}
        </p>
        <Button asChild>
          <Link to="/login?redirect=/history">
            {getHistoryTranslation('signIn', language)}
          </Link>
        </Button>
      </div>
    );
  }

  if (historyLoading || statsLoading) {
    return (
      <div className="container py-16 flex justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  if (historyError) {
    return (
      <div className="container py-16">
        <Alert variant="destructive">
          <AlertDescription>
            {getHistoryTranslation('loadError', language)}
          </AlertDescription>
        </Alert>
      </div>
    );
  }

  return (
    <div className="container py-8">
      {/* Header */}
      <div className="mb-8">
        <h1 className="text-3xl font-bold mb-2">
          {getHistoryTranslation('pageTitle', language)}
        </h1>
        <p className="text-muted-foreground">
          {getHistoryTranslation('pageSubtitle', language)}
        </p>
      </div>

      {/* Stats cards */}
      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
          <Card>
            <CardContent className="pt-6 text-center">
              <div className="text-3xl font-bold text-primary">{stats.total_sessions}</div>
              <div className="text-sm text-muted-foreground">
                {getHistoryTranslation('sessionsStarted', language)}
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-6 text-center">
              <div className="text-3xl font-bold text-green-600">{stats.completed_sessions}</div>
              <div className="text-sm text-muted-foreground">
                {getHistoryTranslation('completed', language)}
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-6 text-center">
              <div className="text-3xl font-bold text-primary">
                {formatDuration(stats.total_time_listened)}
              </div>
              <div className="text-sm text-muted-foreground">
                {getHistoryTranslation('timeListened', language)}
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-6 text-center">
              <div className="text-3xl font-bold text-primary">
                {stats.favorite_category
                  ? getCategoryLabel(stats.favorite_category, language)
                  : '-'}
              </div>
              <div className="text-sm text-muted-foreground">
                {getHistoryTranslation('favoriteCategory', language)}
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* History list or empty state */}
      {history && history.length > 0 ? (
        <div className="space-y-4">
          <h2 className="text-xl font-semibold">
            {getHistoryTranslation('recentSessions', language)}
          </h2>
          {history.map((item: SessionHistoryLocalizedItem) => {
            // The history request carries ?lang=, so the title is already in the chosen language
            const title = item.title;
            const categoryLabel = getCategoryLabel(item.category, language);

            return (
              <Link
                key={`${item.session_id}-${item.last_played}`}
                to={ROUTES.sessionDetail(item.session_id)}
                className="block"
              >
                <Card className="hover:shadow-md transition-shadow">
                  <CardContent className="py-4">
                    <div className="flex items-center justify-between">
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-1">
                          <span className="text-xs font-medium text-primary uppercase">
                            {categoryLabel}
                          </span>
                          {item.completed && (
                            <span className="flex items-center text-xs text-green-600">
                              <CheckCircle className="h-3 w-3 mr-1" />
                              {getHistoryTranslation('completed', language)}
                            </span>
                          )}
                        </div>
                        <h3 className="font-semibold">{title}</h3>
                        <div className="flex items-center gap-4 mt-1 text-sm text-muted-foreground">
                          <span className="flex items-center">
                            <Clock className="h-3 w-3 mr-1" />
                            {formatDuration(item.duration)}
                          </span>
                          <span>{formatPlayedDate(item.last_played, language)}</span>
                        </div>
                      </div>
                      {!item.completed && item.progress > 0 && (
                        <div className="w-24">
                          <div className="h-2 bg-muted rounded-full">
                            <div
                              className="h-full bg-primary rounded-full"
                              style={{ width: `${item.progress}%` }}
                            />
                          </div>
                          <p className="text-xs text-muted-foreground text-center mt-1">
                            {item.progress}%
                          </p>
                        </div>
                      )}
                    </div>
                  </CardContent>
                </Card>
              </Link>
            );
          })}
        </div>
      ) : (
        <div className="text-center py-16">
          <History className="h-16 w-16 mx-auto text-muted-foreground mb-4" />
          <h2 className="text-xl font-semibold mb-2">
            {getHistoryTranslation('noHistory', language)}
          </h2>
          <p className="text-muted-foreground mb-6">
            {getHistoryTranslation('noHistoryDesc', language)}
          </p>
          <Button asChild>
            <Link to={ROUTES.guided}>
              {getHistoryTranslation('browseSessions', language)}
            </Link>
          </Button>
        </div>
      )}
    </div>
  );
}
